import { unionBy } from 'lodash';
import type { Reference, StoreObject } from '@apollo/client';
import type { CollectionAsset } from '../../../generated';
import type { TypePolicies } from '../../../utils';

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      collectionAssets: {
        keyArgs: ['query'],
        merge(existing, incoming, { args, toReference }) {
          if (!existing) return incoming;

          let nodes = existing.nodes;
          if (!args?.page?.after) {
            /** if we're requesting the first page, reset the nodes */
            nodes = [];
          } else {
            /**
             * if we're requesting a subsequent page:
             *
             * - see if the first incoming node is already duped in the
             *   cache
             *
             * - if it is, assume that we have fetched in the middle. all
             *   subsequent nodes beyond that point need to be refetched
             *
             * - if it isn't, assume that all incoming nodes are new and
             *   append them to the existing nodes
             *
             * */
            const firstNode = incoming.nodes?.[0];

            const firstDupedIndex = nodes.findIndex(
              (node: CollectionAsset | Reference) => node === firstNode
            );
            if (firstDupedIndex !== -1) {
              nodes = nodes.slice(0, firstDupedIndex);
            }
          }

          /**
           * finally, we have to remove any of the existing nodes that are
           * duped by the incoming nodes. this addresses the edge case where
           * nodes have changed order from the existing set to the incoming
           * set in the time the request has been in flight
           */
          nodes = nodes.filter(
            (node: CollectionAsset | Reference) =>
              !incoming?.nodes.find(
                (incomingNode: CollectionAsset | Reference) =>
                  node === incomingNode
              )
          );

          nodes.push(...incoming.nodes);

          return {
            ...existing,
            ...incoming,
            nodes,
            groups: unionBy(
              existing?.groups,
              incoming?.groups,
              (group: StoreObject | Reference) => toReference(group).__ref
            ),
          };
        },
      },
    },
  },

  /**
   * Not normalizing CollectionAssetsSortValue because the id for that field is
   * actually the fieldDefinitionId of the FieldValue type. That means that each
   * group will have a set with the same ids, but pointing to different values.
   * For example:
   *
   * groups: [
   *   0 ==> {
   *     id: 1,
   *     values: [
   *       0 ==> {
   *         id: 'a',
   *         value: 0,
   *       }
   *     ],
   *   },
   *   1 ==> {
   *     id: 2,
   *     values: [
   *       0 ==> {
   *         id: 'a', <-- same id as above group
   *         value: 1, <-- different value
   *       },
   *     },
   *   },
   * ]
   */
  CollectionAssetsSortValue: {
    keyFields: false,
  },
};
